import React, { FC, useCallback, useEffect } from "react";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { createFieldBlurHandler } from "utils/formValidation";
import { connect } from "react-redux";
import {
  ButtonsWrapper,
  Content,
  FormWrapper,
} from "../../../../components/Addresses/components/AddressForm.styles";
import Button from "../../../../components/Button/Button";
import Input from "../../../../components/Input/Input";
import {
  createPrintUser,
  updatePrintUser,
} from "../../../../reduxFolder/reducers/printUsers";
import { closeRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  printUserFormHelper,
  PrintUserFormValues,
} from "../../helpers/printUserFormHelper";

interface PrintUserFormPanelProps {
  panelData: any;
  isOpen: boolean;
  closeRightPanel: () => void;
  createPrintUser: (data: any) => Promise<any>;
  updatePrintUser: (id: number, data: any) => Promise<any>;
}

const PrintUserFormPanel: FC<PrintUserFormPanelProps> = ({
  panelData,
  isOpen,
  closeRightPanel,
  createPrintUser,
  updatePrintUser,
}) => {
  const { t } = useTranslation("common");
  const existingUser = panelData.get("printUser");
  const isEditMode = !!existingUser;

  const submitAction = useCallback(
    (data: any) => {
      if (isEditMode) {
        return updatePrintUser(existingUser.get("id"), data);
      }
      return createPrintUser(data);
    },
    [isEditMode, existingUser, createPrintUser, updatePrintUser],
  );

  const initialValues: Partial<PrintUserFormValues> | undefined = isEditMode
    ? {
        email: existingUser.get("email") || "",
      }
    : undefined;

  const formProps = useFormik(
    printUserFormHelper(t, submitAction, closeRightPanel, isEditMode, initialValues),
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleReset,
    isSubmitting,
  } = formProps;
  const handleBlur = createFieldBlurHandler(formProps);

  useEffect(() => {
    if (!isOpen) handleReset(null);
  }, [isOpen]);

  return (
    <ContentWrapper>
      <Heading>
        {isEditMode ? t("tools.editPrintUser") : t("tools.createPrintUser")}
      </Heading>
      <FormWrapper onSubmit={handleSubmit}>
        <Content>
          <Input
            placeholder={t("tools.email")}
            name="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            tooltip={touched.email ? (errors.email as string) : ""}
            invalid={touched.email && !!errors.email}
          />
          {!isEditMode && (
            <>
              <Input
                placeholder={t("tools.uid")}
                name="uid"
                value={values.uid}
                onChange={handleChange}
                onBlur={handleBlur}
                tooltip={touched.uid ? (errors.uid as string) : ""}
                invalid={touched.uid && !!errors.uid}
              />
              <Input
                placeholder={t("tools.password")}
                name="password"
                type="password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                tooltip={touched.password ? (errors.password as string) : ""}
                invalid={touched.password && !!errors.password}
              />
              <Input
                placeholder={t("tools.passwordConfirmation")}
                name="password_confirmation"
                type="password"
                value={values.password_confirmation}
                onChange={handleChange}
                onBlur={handleBlur}
                tooltip={
                  touched.password_confirmation
                    ? (errors.password_confirmation as string)
                    : ""
                }
                invalid={
                  touched.password_confirmation &&
                  !!errors.password_confirmation
                }
              />
            </>
          )}
        </Content>
        <ButtonsWrapper>
          <Button onClick={closeRightPanel}>{t("common.cancel")}</Button>
          <Button
            isLoading={isSubmitting}
            disabled={isSubmitting}
            type={ButtonType.Submit}
            color={ButtonColor.Primary}
            variant={ButtonVariant.Filled}
          >
            {isEditMode ? t("tools.editPrintUser") : t("tools.createPrintUser")}
          </Button>
        </ButtonsWrapper>
      </FormWrapper>
    </ContentWrapper>
  );
};

const withConnect = connect(null, {
  createPrintUser,
  updatePrintUser,
  closeRightPanel,
});

export default withConnect(PrintUserFormPanel);
